'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowUpRight, Printer, Scissors, Lightbulb, Gift, ChevronRight } from 'lucide-react';

export function ServicesBentoGrid() {

  return (
    <section className="relative py-24 bg-[#1A1A1D] w-full overflow-x-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-10 top-10 h-64 w-64 rounded-full bg-[#F1EFEC]/5 blur-3xl" />
        <div className="absolute -right-10 bottom-10 h-64 w-64 rounded-full bg-[#F1EFEC]/5 blur-3xl" />
        <div className="absolute left-1/2 top-1/3 h-32 w-32 -translate-x-1/2 rounded-full bg-[#F1EFEC]/5 blur-2xl" />
      </div>

      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-5" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="inline-block rounded-full bg-[#F1EFEC]/10 px-4 py-1 text-sm font-medium text-[#F1EFEC]/80 mb-4">
            Expert Manufacturing Services
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
            Crafting Excellence Beyond Products
          </h2>
          <p className="mt-4 text-lg text-[#F1EFEC]/70 max-w-2xl mx-auto">
            Discover our comprehensive suite of precision manufacturing services, bringing your creative concepts to reality
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Row 1 */}
          <motion.div
            className="group relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <Link href="/page/3d-printing-services" className="block h-full">
              <div className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#2A2A2D] to-[#1A1A1D] p-8 shadow-lg border border-[#F1EFEC]/5 transition-all duration-300 hover:shadow-xl hover:border-[#F1EFEC]/10">
                <div className="flex items-start justify-between">
                  <div className="max-w-[80%]">
                    <h3 className="text-2xl font-bold text-white mb-3">3D Printing Services</h3>
                    <p className="text-[#F1EFEC]/70 mb-6">Advanced additive manufacturing with precision and detail. From prototypes to production parts, our 3D printing capabilities bring your designs to life with exceptional accuracy.</p>
                  </div>
                  <div className="rounded-full bg-[#F1EFEC]/10 p-4 text-white transition-all duration-300 group-hover:bg-[#F1EFEC]/15 group-hover:scale-110">
                    <Printer className="h-8 w-8" />
                  </div>
                </div>
                <motion.div
                  className="inline-flex items-center text-[#F1EFEC] font-medium mt-2"
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Explore our capabilities <ChevronRight className="ml-1 h-4 w-4" />
                </motion.div>
              </div>
            </Link>
          </motion.div>

          {/* Laser Cutting Services */}
          <motion.div
            className="group relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Link href="/page/laser-cutting-services" className="block h-full">
              <div className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#2A2A2D] to-[#1A1A1D] p-8 shadow-lg border border-[#F1EFEC]/5 transition-all duration-300 hover:shadow-xl hover:border-[#F1EFEC]/10">
                <div className="flex items-start justify-between">
                  <div className="max-w-[80%]">
                    <h3 className="text-2xl font-bold text-white mb-3">Laser Cutting Services</h3>
                    <p className="text-[#F1EFEC]/70 mb-6">Precision laser cutting technology for intricate designs and custom projects. Create detailed components with clean edges and exceptional accuracy.</p>
                  </div>
                  <div className="rounded-full bg-[#F1EFEC]/10 p-4 text-white transition-all duration-300 group-hover:bg-[#F1EFEC]/15 group-hover:scale-110">
                    <Scissors className="h-8 w-8" />
                  </div>
                </div>
                <motion.div
                  className="inline-flex items-center text-[#F1EFEC] font-medium mt-2"
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  View our portfolio <ChevronRight className="ml-1 h-4 w-4" />
                </motion.div>
              </div>
            </Link>
          </motion.div>

          {/* Row 2 */}
          {/* Unique Signs */}
          <motion.div
            className="group relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Link href="/page/unique-signs" className="block h-full">
              <div className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#2A2A2D] to-[#1A1A1D] p-8 shadow-lg border border-[#F1EFEC]/5 transition-all duration-300 hover:shadow-xl hover:border-[#F1EFEC]/10">
                <div className="flex items-start justify-between">
                  <div className="max-w-[80%]">
                    <h3 className="text-2xl font-bold text-white mb-3">Unique Signs & Displays</h3>
                    <p className="text-[#F1EFEC]/70 mb-6">Stand out with custom-designed signage that captures attention. From business displays to home décor, our signs combine aesthetics with durability.</p>
                  </div>
                  <div className="rounded-full bg-[#F1EFEC]/10 p-4 text-white transition-all duration-300 group-hover:bg-[#F1EFEC]/15 group-hover:scale-110">
                    <Lightbulb className="h-8 w-8" />
                  </div>
                </div>
                <motion.div
                  className="inline-flex items-center text-[#F1EFEC] font-medium mt-2"
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Browse our gallery <ChevronRight className="ml-1 h-4 w-4" />
                </motion.div>
              </div>
            </Link>
          </motion.div>

          {/* Personalized Gifts */}
          <motion.div
            className="group relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link href="/page/personalized-gifts" className="block h-full">
              <div className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#2A2A2D] to-[#1A1A1D] p-8 shadow-lg border border-[#F1EFEC]/5 transition-all duration-300 hover:shadow-xl hover:border-[#F1EFEC]/10">
                <div className="flex items-start justify-between">
                  <div className="max-w-[80%]">
                    <h3 className="text-2xl font-bold text-white mb-3">Personalized Gifts</h3>
                    <p className="text-[#F1EFEC]/70 mb-6">Thoughtfully crafted gifts that tell your unique story. Our personalized creations combine artistry with sentiment for truly memorable presents.</p>
                  </div>
                  <div className="rounded-full bg-[#F1EFEC]/10 p-4 text-white transition-all duration-300 group-hover:bg-[#F1EFEC]/15 group-hover:scale-110">
                    <Gift className="h-8 w-8" />
                  </div>
                </div>
                <motion.div
                  className="inline-flex items-center text-[#F1EFEC] font-medium mt-2"
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Discover our collection <ChevronRight className="ml-1 h-4 w-4" />
                </motion.div>
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Explore More Button */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <Link href="/search" className="group inline-flex items-center justify-center">
            <motion.div
              className="rounded-full bg-[#F1EFEC]/10 px-8 py-4 text-white font-medium transition-all duration-300 border border-transparent hover:border-[#F1EFEC]/20 hover:bg-[#F1EFEC]/15"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.2 }}
            >
              <span className="flex items-center">
                Explore Our Full Range of Services
                <motion.span
                  className="ml-2 inline-block"
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowUpRight className="h-5 w-5" />
                </motion.span>
              </span>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
